import { useEffect } from "react";
/**
 * Warnt beim ECHTEN Verlassen der Seite, solange `aktiv` gilt — Tab schliessen, neu
 * laden, Adresse überschreiben, auf dem iPad in eine andere App wechseln und die Seite
 * dabei verwerfen lassen.
 *
 * Abgrenzung zum Verlassen-Wächter aus `useRoute` (web): Der deckt die Navigation
 * INNERHALB der Anwendung ab (programmatischer Sprung und Zurück-Taste) und stellt dort
 * eine eigene, deutsche Frage. Er sieht aber nichts von dem, was der Browser selbst tut —
 * genau diese Lücke schliesst dieser Haken. Beide laufen nebeneinander, ohne sich ins
 * Gehege zu kommen: Zu einem `beforeunload` kommt es nie durch eine SPA-Navigation.
 *
 * Der Text des Dialogs lässt sich seit Jahren nicht mehr bestimmen — jeder aktuelle
 * Browser zeigt seine eigene Formulierung. Deshalb steht hier bewusst KEINE Meldung,
 * sondern nur das Signal "es gibt etwas zu verlieren"; die eigene Formulierung bleibt
 * dem SPA-Wächter vorbehalten, wo sie tatsächlich ankommt.
 *
 * Der Haken liegt in `ui` und nicht in `web`: Er kennt weder Fachlichkeit noch API noch
 * Router, sondern nur ein Fensterereignis — dieselbe Art Baustein wie `useFocusTrap`,
 * `useSizeClass` und `useRecordLock`, die hier bereits wohnen. In `web` läge er zwar
 * näher an seinen heutigen Aufrufern, wäre aber für die Formularbausteine dieses Pakets
 * (Blatt, siehe scripts/check-boundaries.mjs) unerreichbar.
 */
export function useBeforeUnload(aktiv) {
    useEffect(() => {
        // Ohne ungespeicherte Änderungen wird gar nicht erst zugehört: Ein registrierter
        // beforeunload-Zuhörer kostet in manchen Browsern den Vor-/Zurück-Zwischenspeicher
        // (bfcache), und das für nichts.
        if (!aktiv)
            return;
        function beiVerlassen(ereignis) {
            if (verlassenBestaetigt)
                return;
            // Beide Wege setzen, weil die Browser sich nicht einig sind: Die heutige
            // Spezifikation verlangt preventDefault(), ältere Fassungen (und Safari) hängen
            // weiterhin an einem gesetzten returnValue. Wer nur eins tut, bekommt je nach
            // Browser gar keinen Dialog.
            ereignis.preventDefault();
            ereignis.returnValue = "";
        }
        window.addEventListener("beforeunload", beiVerlassen);
        return () => window.removeEventListener("beforeunload", beiVerlassen);
    }, [aktiv]);
}
/** Ob das Verlassen schon bestätigt ist (`verlassenFreigeben`) — Modulzustand bis zum Neuladen. */
let verlassenBestaetigt = false;
/**
 * Ein schon BESTÄTIGTES Verlassen, unmittelbar bevor die Seite neu lädt (Wechsel des aktiven Unternehmens,
 * Spec Mandantentrennung R4). Bestätigt ist es in genau zwei Lagen: Der Wächter der Anwendung hat GEFRAGT und
 * der Benutzer „Verlassen" gewählt — oder der Aufrufer hat selbst festgestellt, dass nichts verloren geht, und
 * deshalb nicht gefragt (`ungefragt`, etwa der Dublettentreffer mit nur dem Suchnamen, R20). Danach widerspricht
 * kein Haken mehr — der Browser stellte sonst eine zweite Frage, und ein „Bleiben" dort ließe das Fenster im
 * alten Zusammenhang stehen, obwohl die Wahl schon gilt. Gilt bis zum Neuladen. In keiner anderen Lage
 * aufrufen: Ohne Wächter der Anwendung ist der Browser die einzige Absicherung einer offenen Maske.
 */
export function verlassenFreigeben() {
    verlassenBestaetigt = true;
}
/**
 * Nimmt die Freigabe zurück — NUR für Tests (wie `offeneEbenen`): Der Zustand ist Modulzustand und überlebt
 * sonst von einem Test in den nächsten. Keine Anwendungs-API — die Anwendung lädt nach einer Freigabe neu.
 */
export function verlassenFreigabeZuruecknehmen() {
    verlassenBestaetigt = false;
}
//# sourceMappingURL=useBeforeUnload.js.map